import type { ReactNode } from "react";
import { AlertCircle, CheckCircle2, Info, Loader2 } from "lucide-react";
import { ApiError } from "@/lib/api";
import { cn } from "@/lib/utils";

/** Problem details summary: localized detail/title plus every validation message. */
export function ProblemAlert({ error, className }: { error: unknown; className?: string }) {
  if (!error) return null;
  const messages =
    error instanceof ApiError
      ? error.allMessages
      : [error instanceof Error ? error.message : "Something went wrong."];
  const title = error instanceof ApiError && Object.keys(error.errors).length ? error.title : undefined;
  return (
    <div
      role="alert"
      className={cn(
        "flex gap-3 rounded-md border border-destructive/40 bg-destructive/5 px-4 py-3 text-sm text-destructive",
        className,
      )}
    >
      <AlertCircle className="mt-0.5 size-4 shrink-0" />
      <div className="space-y-1">
        {title && <p className="font-medium">{title}</p>}
        {messages.map((m) => (
          <p key={m}>{m}</p>
        ))}
        {error instanceof ApiError && error.traceId && (
          <p className="text-[0.7rem] text-destructive/60">Reference: {error.traceId}</p>
        )}
      </div>
    </div>
  );
}

/** Messages of one field from client validation or the server's "errors" dictionary. */
export function FieldError({ messages }: { messages?: (string | null | undefined)[] | undefined }) {
  const list = (messages ?? []).filter(Boolean);
  if (!list.length) return null;
  return (
    <div className="space-y-0.5">
      {list.map((m) => (
        <p key={m} className="text-xs text-destructive">
          {m}
        </p>
      ))}
    </div>
  );
}

export function fieldErrors(error: unknown, name: string): string[] | undefined {
  return error instanceof ApiError ? error.field(name) : undefined;
}

export function Notice({
  tone = "info",
  title,
  children,
  className,
}: {
  tone?: "info" | "success" | "warning";
  title?: string;
  children?: ReactNode;
  className?: string;
}) {
  const Icon = tone === "success" ? CheckCircle2 : tone === "warning" ? AlertCircle : Info;
  return (
    <div
      className={cn(
        "flex gap-3 rounded-md border px-4 py-3 text-sm",
        tone === "success" && "border-gold/50 bg-gold/10",
        tone === "warning" && "border-destructive/30 bg-destructive/5",
        tone === "info" && "border-border bg-secondary",
        className,
      )}
    >
      <Icon className={cn("mt-0.5 size-4 shrink-0", tone === "warning" ? "text-destructive" : "text-gold")} />
      <div className="space-y-1">
        {title && <p className="font-medium text-foreground">{title}</p>}
        {children && <div className="text-muted-foreground">{children}</div>}
      </div>
    </div>
  );
}

export function Loading({ label = "Loading…", className }: { label?: string; className?: string }) {
  return (
    <div className={cn("flex items-center gap-3 py-10 text-sm text-muted-foreground", className)}>
      <Loader2 className="size-4 animate-spin text-gold" />
      {label}
    </div>
  );
}

export function EmptyState({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div className="rounded-lg border border-dashed border-border bg-card/50 px-6 py-12 text-center">
      <p className="font-display text-2xl">{title}</p>
      {children && <div className="mt-2 text-sm text-muted-foreground">{children}</div>}
    </div>
  );
}
